import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { ProgressionHistory } from '../../services/api/ProgramApiService';

interface ProgramProgressChartProps {
  history: ProgressionHistory[];
}

export const ProgramProgressChart: React.FC<ProgramProgressChartProps> = ({ history }) => {
  const { t } = useLanguage();
  const [selectedExercise, setSelectedExercise] = useState<number>(
    history.length > 0 ? history[0].program_exercise_id : 0
  );

  const selected = history.find((h) => h.program_exercise_id === selectedExercise);

  const chartData = selected
    ? selected.history.map((p) => ({
        date: p.completed_at.substring(0, 10),
        weight: Number(p.weight_kg),
      }))
    : [];

  if (history.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {t('programs.noProgress')}
      </Typography>
    );
  }

  return (
    <Box>
      <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
        <InputLabel>{t('programs.exercises')}</InputLabel>
        <Select
          value={selectedExercise}
          label={t('programs.exercises')}
          onChange={(e) => setSelectedExercise(Number(e.target.value))}
        >
          {history.map((h) => (
            <MenuItem key={h.program_exercise_id} value={h.program_exercise_id}>
              {h.exercise_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis unit=" kg" tick={{ fontSize: 11 }} />
          <Tooltip formatter={(value: number) => [`${value} kg`, t('programs.currentWeight')]} />
          <Legend />
          <Line
            type="monotone"
            dataKey="weight"
            name={selected?.exercise_name ?? ''}
            stroke="#1976d2"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
